import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, Checkbox, ColorInput, FileButton, Group, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPalette, IconSparkles, IconUpload, IconX } from '@tabler/icons-react';
import {
  BRAND_KIT_COPY as K,
  ROKU_BRAND_KIT_COPY as R,
  TV_BRAND_KIT_COPY as C,
  TV_TILE_TEXT_MAX,
  type FactoryAssetPlatform,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import type { FactoryBrandKit, FactoryBrandKitGenerateReq, FactoryBrandKitSeed } from '@/lib/types/AppFactory';
import { BrandKitUtils, type BrandKitLiveKind } from '../BrandKitUtils';

interface BrandKitFormProps {
  /** The stored kit; null = none yet. */
  kit: FactoryBrandKit | null;
  /** What a first kit can start from (only while there is no kit). */
  seed: FactoryBrandKitSeed | null;
  /** The image sets of the TV apps that are on. */
  scopes: readonly FactoryAssetPlatform[];
  /** The TV platforms that are on: every sentence names only these. */
  tv: readonly TvPlatformKey[];
  /** What each image set's live images are (a set with its own art starts unticked). */
  liveKinds: Partial<Record<FactoryAssetPlatform, BrandKitLiveKind>>;
  /** The image sets a Generate makes — owned by the tab, so they outlive this form's remount. */
  ticked: readonly FactoryAssetPlatform[];
  onTickedChange: (next: FactoryAssetPlatform[]) => void;
  /** The app name the form starts with when the kit has none. */
  defaultAppName: string;
  /** Drafts from the kit are waiting above: a new Generate replaces them. */
  hasDrafts?: boolean;
  disabled?: boolean;
  generating?: boolean;
  onGenerate: (req: FactoryBrandKitGenerateReq) => void;
}

/**
 * The app's brand inputs, once (app-level Brand Kit, 3.42.0): a square symbol,
 * an optional wide wordmark, the app name set in type when there is no
 * wordmark, the ground / text / accent colours and — while Fire TV or Google
 * TV is on — optional 16:9 tile art. It opens on what the factory has stored
 * (or on the Roku logo a first kit is seeded from), so a change is one field
 * and a Generate, and a source already stored is never uploaded again. Client
 * checks are advice only; the factory judges. The parent remounts it (by key)
 * when the stored kit changes.
 */
export function BrandKitForm({
  kit,
  seed,
  scopes,
  tv,
  liveKinds,
  ticked,
  onTickedChange,
  defaultAppName,
  hasDrafts,
  disabled,
  generating,
  onGenerate,
}: BrandKitFormProps) {
  const [symbol, setSymbol] = useState<File | null>(null);
  const [symbolAdvice, setSymbolAdvice] = useState<string[]>([]);
  const [suggestedHex, setSuggestedHex] = useState<string | null>(null);
  const [wordmark, setWordmark] = useState<File | null>(null);
  const [wordmarkAdvice, setWordmarkAdvice] = useState<string[]>([]);
  const [removeWordmark, setRemoveWordmark] = useState(false);
  const [tileArt, setTileArt] = useState<File | null>(null);
  const [removeTileArt, setRemoveTileArt] = useState(false);
  const [appName, setAppName] = useState((kit ? kit.appName : defaultAppName).slice(0, TV_TILE_TEXT_MAX));
  const [bgColorHex, setBgColorHex] = useState(kit?.bgColorHex || seed?.bgColorHex || BrandKitUtils.PRESETS[0].bgColorHex);
  // A seeded kit takes the Roku poster's ground, which may be light: the text starts readable on it.
  const [textColorHex, setTextColorHex] = useState(
    kit ? kit.textColorHex : seed?.bgColorHex ? BrandKitUtils.textOnGround(seed.bgColorHex) : BrandKitUtils.PRESETS[0].textColorHex,
  );
  const [accentColorHex, setAccentColorHex] = useState(kit?.accentColorHex ?? '');
  // The picked files are being read for the request: the button is busy from the click, not from the POST.
  const [reading, setReading] = useState(false);
  // The newest symbol / wordmark pick: a slower pixel read of an earlier pick must not set its advice.
  const symbolPick = useRef(0);
  const wordmarkPick = useRef(0);

  const googletv = tv.includes('googletv');
  const firetv = tv.includes('firetv');
  const androidFamily = scopes.includes('firetv');
  const storedSymbolUrl = kit?.symbolUrl ?? (!kit && seed?.as === 'symbol' ? seed.logoUrl : undefined);
  const storedWordmarkUrl = kit?.wordmarkUrl ?? (!kit && seed?.as === 'wordmark' ? seed.logoUrl : undefined);
  const storedCaption = kit ? K.storedSource : K.seededSource;
  const hasMark = BrandKitUtils.hasMark({
    symbolPicked: !!symbol,
    wordmarkPicked: !!wordmark,
    removeWordmark,
    kit,
    seed,
  });
  const showTicks = scopes.length > 1 || scopes.some((s) => liveKinds[s] === 'custom');

  const tooBig = (file: File) => {
    if (file.size <= BrandKitUtils.MAX_LOGO_BYTES) return false;
    notifications.show({ title: 'File too large', message: 'Images must be under 8MB.', color: 'red' });
    return true;
  };

  const pickSymbol = async (file: File | null) => {
    if (!file || tooBig(file)) return;
    const pick = ++symbolPick.current;
    setSymbol(file);
    const px = await BrandKitUtils.readImagePixels(file);
    if (pick !== symbolPick.current) return;
    setSymbolAdvice(BrandKitUtils.symbolAdvice(px ? { w: px.w, h: px.h } : null, px ? BrandKitUtils.hasTransparency(px.rgba) : null));
    setSuggestedHex(px ? BrandKitUtils.dominantBrandColorHex(px.rgba) : null);
  };

  const pickWordmark = async (file: File | null) => {
    if (!file || tooBig(file)) return;
    const pick = ++wordmarkPick.current;
    setWordmark(file);
    setRemoveWordmark(false);
    const px = await BrandKitUtils.readImagePixels(file);
    if (pick !== wordmarkPick.current) return;
    setWordmarkAdvice(px && !BrandKitUtils.hasTransparency(px.rgba) ? [C.noTransparency] : []);
  };

  const pickTileArt = (file: File | null) => {
    if (!file || tooBig(file)) return;
    setTileArt(file);
    setRemoveTileArt(false);
  };

  const toggle = (scope: FactoryAssetPlatform, on: boolean) =>
    onTickedChange(scopes.filter((s) => (s === scope ? on : ticked.includes(s))));

  const generate = async () => {
    if (reading) return;
    setReading(true);
    try {
      const [symbolBase64, wordmarkBase64, tileArtBase64] = await Promise.all(
        [symbol, wordmark, androidFamily ? tileArt : null].map((f) => (f ? BrandKitUtils.fileToBase64(f) : Promise.resolve(null))),
      );
      onGenerate(
        BrandKitUtils.kitFormRequest({
          platforms: showTicks ? [...ticked] : [...scopes],
          symbolBase64,
          wordmarkBase64,
          tileArtBase64,
          removeWordmark,
          removeTileArt,
          appName,
          bgColorHex,
          textColorHex,
          accentColorHex,
        }),
      );
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Could not read the images.',
        color: 'red',
      });
    } finally {
      setReading(false);
    }
  };

  const symbolWhy = googletv ? C.symbolWhy : firetv ? C.symbolWhyFireTv : R.symbolWhy;
  const tileArtHelp = firetv && googletv ? C.tileArtHelpBoth : googletv ? C.tileArtHelpGoogleTv : C.tileArtHelp;
  const noTicks = showTicks && ticked.length === 0;
  // An empty ground would read as "render the stored kit as it is" at the factory.
  const noGround = !BrandKitUtils.isHexColor(bgColorHex);

  return (
    <Card withBorder radius="md" p="md" data-testid="brand-kit-form">
      <Stack gap="md">
        <Group gap="xs">
          <IconPalette size={20} />
          <Text fw={600} size="md">
            {hasDrafts ? K.formTitleDrafts : K.formTitle}
          </Text>
        </Group>

        {!kit && seed && (
          <Alert icon={<IconSparkles size={16} />} color="blue" data-testid="brand-kit-seeded">
            {K.seededNotice}
            {seed.as === 'wordmark' && (googletv || firetv) ? ` ${googletv ? K.seededSymbolNudge : K.seededSymbolNudgeIcons}` : ''}
          </Alert>
        )}

        <SourceSlot
          label={C.symbolLabel}
          chooseLabel={`Choose ${C.symbolLabel} (PNG)`}
          accept="image/png"
          why={symbolWhy}
          help={C.symbolHelp}
          picked={symbol}
          storedUrl={storedSymbolUrl}
          storedCaption={storedCaption}
          ground={bgColorHex}
          onPick={(f) => void pickSymbol(f)}
          onClearPick={() => {
            symbolPick.current++;
            setSymbol(null);
            setSymbolAdvice([]);
            setSuggestedHex(null);
          }}
          advice={symbolAdvice}
          testId="kit-symbol"
        />

        <SourceSlot
          label="Wordmark"
          chooseLabel={`Choose ${C.wordmarkLabel}`}
          accept="image/png"
          help={C.wordmarkHelp}
          picked={wordmark}
          storedUrl={storedWordmarkUrl}
          storedCaption={storedCaption}
          removed={removeWordmark}
          onRemove={() => setRemoveWordmark(true)}
          onRestore={() => setRemoveWordmark(false)}
          ground={bgColorHex}
          onPick={(f) => void pickWordmark(f)}
          onClearPick={() => {
            wordmarkPick.current++;
            setWordmark(null);
            setWordmarkAdvice([]);
          }}
          advice={wordmarkAdvice}
          testId="kit-wordmark"
        />

        <TextInput
          label={C.appNameLabel}
          description={C.appNameHelp}
          value={appName}
          maxLength={TV_TILE_TEXT_MAX}
          onChange={(e) => setAppName(e.currentTarget.value)}
          maw={420}
        />

        <Stack gap={6}>
          <Group align="flex-end" gap="sm" wrap="wrap">
            <ColorInput label={C.groundLabel} description={C.groundHelp} value={bgColorHex} onChange={setBgColorHex} w={260} />
            <ColorInput label="Text" value={textColorHex} onChange={setTextColorHex} w={150} />
            <ColorInput label="Accent" placeholder="Optional" value={accentColorHex} onChange={setAccentColorHex} w={150} />
          </Group>
          <Group gap="xs">
            {suggestedHex && (
              <Button size="compact-xs" variant="light" onClick={() => setBgColorHex(suggestedHex)} data-testid="suggested-ground">
                {C.suggestedGround} ({suggestedHex})
              </Button>
            )}
            <Text size="xs" c="dimmed">
              Presets:
            </Text>
            {BrandKitUtils.PRESETS.map((p) => (
              <Button
                key={p.key}
                size="compact-xs"
                variant="light"
                onClick={() => {
                  setBgColorHex(p.bgColorHex);
                  setTextColorHex(p.textColorHex);
                }}
              >
                {p.label}
              </Button>
            ))}
          </Group>
        </Stack>

        {androidFamily && (
          <SourceSlot
            label="Tile art"
            chooseLabel={`Choose ${C.tileArtLabel}`}
            accept="image/png,image/jpeg"
            help={tileArtHelp}
            picked={tileArt}
            storedUrl={kit?.tileArtUrl}
            storedCaption={K.storedSource}
            removed={removeTileArt}
            onRemove={() => setRemoveTileArt(true)}
            onRestore={() => setRemoveTileArt(false)}
            onPick={pickTileArt}
            onClearPick={() => setTileArt(null)}
            testId="kit-tile-art"
          />
        )}

        {showTicks && (
          <Stack gap={6} data-testid="kit-platforms">
            <Text size="sm" fw={500}>
              {K.platformsLabel}
            </Text>
            {scopes.map((scope) => {
              const kind = liveKinds[scope] ?? 'none';
              const note = kind === 'custom' ? K.platformCustom : kind === 'generated' ? K.platformGenerated : K.platformNone;
              return (
                <Checkbox
                  key={scope}
                  checked={ticked.includes(scope)}
                  onChange={(e) => toggle(scope, e.currentTarget.checked)}
                  label={`${BrandKitUtils.scopeLabel(scope, tv)} — ${note}`}
                />
              );
            })}
          </Stack>
        )}

        <Group>
          <Button onClick={() => void generate()} loading={generating || reading} disabled={disabled || !hasMark || noTicks || noGround}>
            {hasDrafts ? K.generateAgain : K.generate}
          </Button>
          <Text size="xs" c={!hasMark || noTicks || noGround ? 'orange' : 'dimmed'}>
            {!hasMark
              ? K.symbolNeeded
              : noGround
                ? K.groundNeeded
                : noTicks
                  ? K.platformsNeeded
                  : hasDrafts
                    ? K.generateAgainNote
                    : K.generateNote}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}

interface SourceSlotProps {
  label: string;
  chooseLabel: string;
  accept: string;
  /** One line beside the button: why this source matters. */
  why?: string;
  help: string;
  picked: File | null;
  /** The source the factory already holds for this slot. */
  storedUrl?: string;
  storedCaption: string;
  /** The stored source is marked to be dropped at the next Generate (optional slots only). */
  removed?: boolean;
  onRemove?: () => void;
  onRestore?: () => void;
  /** The ground colour behind a mark's thumbnail, so a white logo shows. */
  ground?: string;
  onPick: (file: File | null) => void;
  onClearPick: () => void;
  advice?: string[];
  testId: string;
}

/** One brand source: what is stored (a thumbnail), a newly picked file over it, and replace / remove. */
function SourceSlot({
  label,
  chooseLabel,
  accept,
  why,
  help,
  picked,
  storedUrl,
  storedCaption,
  removed,
  onRemove,
  onRestore,
  ground,
  onPick,
  onClearPick,
  advice = [],
  testId,
}: SourceSlotProps) {
  const pickedUrl = useObjectUrl(picked);
  // Mantine's FileButton keeps its input's value: without a reset, picking the
  // same file again after clearing it fires no change.
  const resetPick = useRef<() => void>(null);
  const clearPick = () => {
    resetPick.current?.();
    onClearPick();
  };
  const stored = !!storedUrl && !removed;
  const thumbUrl = pickedUrl ?? (stored ? storedUrl : undefined);
  return (
    <Stack gap={4} data-testid={testId}>
      <Group gap="sm" align="center" wrap="wrap">
        {thumbUrl && (
          <Box
            style={{
              width: 96,
              height: 56,
              flex: 'none',
              borderRadius: 6,
              border: '1px solid var(--mantine-color-gray-3)',
              background: ground || 'var(--mantine-color-gray-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img src={thumbUrl} alt={label} style={{ maxWidth: '88%', maxHeight: '84%', objectFit: 'contain' }} />
          </Box>
        )}
        <FileButton resetRef={resetPick} onChange={onPick} accept={accept}>
          {(props) => (
            <Button variant="default" leftSection={<IconUpload size={14} />} {...props}>
              {picked ? picked.name : stored ? `Replace ${label}` : chooseLabel}
            </Button>
          )}
        </FileButton>
        {picked ? (
          <Button variant="subtle" size="compact-sm" color="gray" leftSection={<IconX size={12} />} onClick={clearPick}>
            {storedUrl ? 'Keep the current one' : 'Clear'}
          </Button>
        ) : stored && onRemove ? (
          <Button variant="subtle" size="compact-sm" color="gray" leftSection={<IconX size={12} />} onClick={onRemove}>
            Remove
          </Button>
        ) : removed && onRestore ? (
          <Button variant="subtle" size="compact-sm" onClick={onRestore}>
            Keep the current {label.toLowerCase()}
          </Button>
        ) : null}
        {!picked && stored && (
          <Text size="xs" c="dimmed">
            {storedCaption}
          </Text>
        )}
        {why && !thumbUrl && (
          <Text size="xs" c="dimmed">
            {why}
          </Text>
        )}
      </Group>
      <Text size="xs" c="dimmed">
        {help}
      </Text>
      {advice.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" py={6}>
          {advice.map((a) => (
            <Text size="sm" key={a}>
              {a}
            </Text>
          ))}
        </Alert>
      )}
    </Stack>
  );
}

/** An object URL for a picked file's thumbnail, released when the file changes or the slot unmounts. */
function useObjectUrl(file: File | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}
